import { useState, useEffect, useRef } from 'react';
import { Package, Tag, Storefront, Gift, ArrowRight, ArrowLeft, User, EnvelopeSimple, Buildings, CheckCircle, ShoppingBag, CalendarBlank } from '@phosphor-icons/react';
import PhoneInput, { isValidPhoneNumber, getCountries } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const EXCLUDED_COUNTRIES = ['IN', 'BD', 'PK'];
const COUNTRIES = getCountries().filter(c => !EXCLUDED_COUNTRIES.includes(c));

const API = '';

// Get or create persistent UUID
function getVisitorUUID() {
  let uuid = localStorage.getItem('sv_uuid');
  if (!uuid) {
    uuid = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('sv_uuid', uuid);
  }
  return uuid;
}

function trackEvent(event, step = null) {
  try {
    const params = new URLSearchParams(window.location.search);
    const source = params.get('utm_source') || params.get('source') || 'direct';
    // Gather full tracking context so events.js can forward to Black Mamba
    // with proper source attribution (referrer + UTMs preserved).
    let bm_session_id = null;
    try { bm_session_id = localStorage.getItem('sv_ss'); } catch (e) {}
    const bm_utm = {
      utm_source:   params.get('utm_source'),
      utm_medium:   params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content:  params.get('utm_content'),
      utm_term:     params.get('utm_term'),
      gclid:        params.get('gclid'),
      fbclid:       params.get('fbclid'),
    };
    fetch('/api/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        uuid: getVisitorUUID(),
        event,
        step,
        source,
        timestamp: new Date().toISOString(),
        // Passed through by events.js when it forwards to Black Mamba
        bm_session_id,
        bm_referrer: document.referrer || null,
        bm_landing: window.location.href,
        bm_utm,
      }),
    }).catch(() => {});
    // NOTE: events.js server-forwards to Black Mamba, so no direct svTrack
    // here — avoids double-counting each event.
  } catch (e) {}
}

const SERVICES = [
  { label: 'Outsourced DTC fulfillment', Icon: Package, key: 'dtc' },
  { label: 'Amazon FBA Prep', Icon: Tag, key: 'fba' },
  { label: 'Wholesale & Retail', Icon: Storefront, key: 'wholesale' },
  { label: 'Subscription Boxes', Icon: Gift, key: 'subscriptions' },
];

const PLATFORMS = [
  { label: 'Shopify', key: 'shopify', img: '/lp/whatweintegrate/shopify.png' },
  { label: 'Amazon', key: 'amazon', img: '/lp/whatweintegrate/amazon.png' },
  { label: 'TikTok Shop', key: 'tiktok', img: '/lp/whatweintegrate/tiktok.png' },
  { label: 'WooCommerce', key: 'woocommerce', img: '/lp/whatweintegrate/WooCommerce.png' },
  { label: 'BigCommerce', key: 'bigcommerce', img: '/lp/whatweintegrate/bigcommerce.png' },
  { label: 'Other', key: 'other', img: null },
];

const VOLUMES = [
  '0 - 50',
  '51 - 499',
  '501 - 1,000',
  '1,000 - 5,000',
  '5,000 - 10,000',
  '10,000 - 50,000',
  '50,000+',
];

// Volumes we consider "low quality" for pixel/scheduling purposes.
// These leads do NOT get the Calendly link and are NOT reported as a Lead
// event to Meta/Google. Routing (Fulfilment.com vs silent) is decided
// server-side in /api/leads.js based on the specific bucket.
const LOW_QUALITY_VOLUMES = new Set(['0 - 50', '51 - 499']);

export default function QuoteFormAd() {
  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [volume, setVolume] = useState('');
  const [challenges, setChallenges] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '' });
  const [honeypot, setHoneypot] = useState(''); // MUST stay empty — bots fill it, humans never see it
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const formStarted = useRef(false);
  const formLoadedAt = useRef(Date.now()); // used server-side to reject sub-4s bot submissions
  const fullSubmitted = useRef(false);     // full submit fired — skip exit beacon
  const exitBeaconSent = useRef(false);    // exit beacon fired — never twice
  const stateRef = useRef({});             // live snapshot for beacons (no stale closure)

  // Track page view on mount.
  // NOTE: sv-tracker.js (Black Mamba beacon) also auto-fires page_view on load,
  // but this call keeps the local KV counter used by /lp/admin populated.
  // events.js dedupes for Black Mamba by session_id + event_type at query time.
  useEffect(() => { trackEvent('page_view'); }, []);

  // Keep a live snapshot for the exit beacon so we don't send stale data.
  useEffect(() => {
    stateRef.current = { step, selected, platforms, volume, challenges, form, honeypot };
  });

  // Exit-intent beacon — if the visitor typed a name+email but never hit the
  // final submit, ship whatever they filled to /api/leads with `exit_intent`
  // set. Uses sendBeacon so it fires even during tab close. Idempotent via
  // exitBeaconSent ref (never twice) and gated by fullSubmitted (skip if the
  // real submit already went out).
  useEffect(() => {
    const fireExitBeacon = () => {
      if (exitBeaconSent.current || fullSubmitted.current) return;
      const s = stateRef.current;
      if (!s || !s.form) return;
      const hasContact = s.form.name.trim().length > 1 && s.form.email.trim().length > 3;
      if (!hasContact) return;
      exitBeaconSent.current = true;
      try {
        const qp = new URLSearchParams(window.location.search);
        const bm_session_id = (function () { try { return localStorage.getItem('sv_ss'); } catch { return null; } })();
        const bm_utm = {
          utm_source:   qp.get('utm_source'),
          utm_medium:   qp.get('utm_medium'),
          utm_campaign: qp.get('utm_campaign'),
          utm_content:  qp.get('utm_content'),
          utm_term:     qp.get('utm_term'),
          gclid:        qp.get('gclid'),
          fbclid:       qp.get('fbclid'),
        };
        const payload = JSON.stringify({
          services: s.selected.length ? s.selected : ['pending'],
          platforms: s.platforms,
          monthly_orders: s.volume || 'pending',
          challenges: s.challenges,
          source: getSource(),
          name: s.form.name,
          email: s.form.email,
          company: s.form.company,
          phone: s.form.phone,
          bm_session_id,
          bm_referrer: document.referrer || null,
          bm_landing: window.location.href,
          bm_utm,
          website_url: s.honeypot,
          bm_form_ms: Date.now() - formLoadedAt.current,
          exit_intent: true,
          last_step_reached: s.step,
        });
        if (navigator.sendBeacon) {
          navigator.sendBeacon('/api/leads', new Blob([payload], { type: 'application/json' }));
        } else {
          fetch('/api/leads', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: payload,
            keepalive: true,
          }).catch(() => {});
        }
      } catch {}
    };
    const onVisibility = () => { if (document.visibilityState === 'hidden') fireExitBeacon(); };
    window.addEventListener('pagehide', fireExitBeacon);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', fireExitBeacon);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Capture UTM source from URL params
  const getSource = () => {
    const params = new URLSearchParams(window.location.search);
    const utm = params.get('utm_source') || params.get('source');
    if (utm) return utm;
    const ref = document.referrer.toLowerCase();
    if (ref.includes('instagram')) return 'instagram';
    if (ref.includes('facebook')) return 'facebook';
    if (ref.includes('google')) return 'google';
    if (ref.includes('tiktok')) return 'tiktok';
    if (ref.includes('youtube')) return 'youtube';
    if (ref.includes('linkedin')) return 'linkedin';
    if (ref.includes('twitter') || ref.includes('x.com')) return 'x';
    if (ref.includes('pinterest')) return 'pinterest';
    if (ref.includes('bing')) return 'bing';
    if (ref.includes('snapchat')) return 'snapchat';
    return 'direct';
  };

  const totalSteps = 6;

  const toggleService = (key) => {
    if (!formStarted.current) {
      formStarted.current = true;
      trackEvent('form_start');
      try { window.svLive && window.svLive.formStart(); } catch {}
    }
    setSelected((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const togglePlatform = (key) => {
    setPlatforms((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const toggleChallenge = (key) => {
    setChallenges((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const canNext = () => {
    // New order:  0=Contact · 1=Service · 2=Platform · 3=Volume · 4=Challenges · 5=Done
    if (step === 0) return form.name.trim() && form.email.trim() && form.company.trim() && form.phone && isValidPhoneNumber(form.phone);
    if (step === 1) return selected.length > 0;
    if (step === 2) return platforms.length > 0;
    if (step === 3) return volume !== '';
    if (step === 4) return challenges.length > 0;
    return true;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    // Gather tracking context so leads.js can fire a server-side event to
    // Black Mamba even if the browser-side svTrack call fails.
    const qp = new URLSearchParams(window.location.search);
    const bm_session_id = (function () {
      try { return localStorage.getItem('sv_ss'); } catch { return null; }
    })();
    const bm_utm = {
      utm_source:   qp.get('utm_source'),
      utm_medium:   qp.get('utm_medium'),
      utm_campaign: qp.get('utm_campaign'),
      utm_content:  qp.get('utm_content'),
      utm_term:     qp.get('utm_term'),
      gclid:        qp.get('gclid'),
      fbclid:       qp.get('fbclid'),
    };
    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          services: selected,
          platforms: platforms,
          monthly_orders: volume,
          challenges: challenges,
          source: getSource(),
          name: form.name,
          email: form.email,
          company: form.company,
          phone: form.phone,
          // Black Mamba tracking context (used server-side as belt-and-suspenders
          // in case the tracker snippet failed to load / was blocked)
          bm_session_id,
          bm_referrer: document.referrer || null,
          bm_landing: window.location.href,
          bm_utm,
          // Abuse-shield fields:
          website_url: honeypot,                            // honeypot — must be empty
          bm_form_ms: Date.now() - formLoadedAt.current,    // time-on-page — rejected if < 4s
        }),
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
    } catch (e) {
      console.error('Form submission error:', e);
    }
    setSubmitting(false);
    setDone(true);
    setStep(5);
    fullSubmitted.current = true;   // block exit beacon — full submit already went out
    trackEvent('form_submit');
    try { window.svLive && window.svLive.formSubmit(); } catch {}
    const isLowQ = LOW_QUALITY_VOLUMES.has(volume);
    // NOTE: lead_qualified / lead_referred events fire from leads.js
    // server-side (via context.waitUntil) — this guarantees Black Mamba
    // attribution even if the browser-side tracker is blocked. Client-side
    // svTrack call is intentionally omitted here to avoid duplicate rows.
    // Only report as a Lead to Meta/Google for QUALIFIED volumes (500+).
    // This keeps ad audiences and conversion optimization clean.
    if (!isLowQ) {
      if (window.fbq) window.fbq('track', 'Lead');
      if (window.gtag) window.gtag('event', 'generate_lead', { event_category: 'form', event_label: 'quote_form' });
    }
  };

  const next = () => {
    if (step === 4) {
      handleSubmit();
    } else {
      const nextStep = step + 1;
      trackEvent('form_step', nextStep);
      try { window.svLive && window.svLive.formStep(nextStep); } catch {}
      setStep(nextStep);
    }
  };

  return (
    <div
      data-testid="hero-quote-card"
      className="relative z-20 w-full max-w-[520px] bg-white rounded-2xl p-6 sm:p-8"
      style={{ boxShadow: '0 8px 50px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.08)' }}
    >
      {/* Honeypot — invisible to humans, irresistible to naive bots.
          Off-screen, aria-hidden, tab-skipped. Any value submitted here = spam. */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
        <label>Website URL (leave blank)
          <input
            type="text"
            name="website_url"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </label>
      </div>

      {/* Step dots */}
      <div className="flex gap-2 justify-center mb-5">
        {[...Array(totalSteps)].map((_, i) => (
          <div
            key={i}
            className={`h-2.5 rounded-full transition-all duration-300 ${
              i === step ? 'w-7 bg-[#07c28e]' : i < step ? 'w-2.5 bg-[#07c28e]/40' : 'w-2.5 bg-gray-200'
            }`}
          />
        ))}
      </div>

      {/* ─── STEP 0: Contact Info (moved to front — capture ASAP) ─── */}
      {step === 0 && (
        <div className="space-y-5" data-testid="quote-step-contact">
          <div className="text-center">
            <p className="text-[10px] sm:text-[11px] font-bold text-[#07c28e] tracking-[0.2em] uppercase mb-1.5">Get 3PL Fulfillment Pricing</p>
            <h2 className="text-lg sm:text-xl font-bold text-[#191E29]">Where should we send your custom pricing?</h2>
          </div>
          <div className="space-y-3">
            <div className="relative">
              <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" placeholder="Full name *" value={form.name}
                onChange={(e) => {
                  if (!formStarted.current) { formStarted.current = true; trackEvent('form_start'); try { window.svLive && window.svLive.formStart(); } catch {} }
                  setForm({ ...form, name: e.target.value });
                }}
                data-testid="quote-input-name"
                className="w-full pl-11 pr-4 py-3.5 border-2 border-gray-100 rounded-xl text-sm text-[#191E29] placeholder-gray-400 focus:border-[#07c28e] focus:outline-none transition-colors" />
            </div>
            <div className="relative">
              <EnvelopeSimple size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="email" placeholder="Work email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="quote-input-email"
                className="w-full pl-11 pr-4 py-3.5 border-2 border-gray-100 rounded-xl text-sm text-[#191E29] placeholder-gray-400 focus:border-[#07c28e] focus:outline-none transition-colors" />
            </div>
            <div className="relative">
              <Buildings size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" placeholder="Company name *" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="quote-input-company"
                className="w-full pl-11 pr-4 py-3.5 border-2 border-gray-100 rounded-xl text-sm text-[#191E29] placeholder-gray-400 focus:border-[#07c28e] focus:outline-none transition-colors" />
            </div>
            <div>
              <PhoneInput
                international
                defaultCountry="US"
                countries={COUNTRIES}
                placeholder="Phone number *"
                value={form.phone}
                onChange={(val) => setForm({ ...form, phone: val || '' })}
                data-testid="quote-input-phone"
                className="phone-input-custom"
              />
              {form.phone && !isValidPhoneNumber(form.phone) && (
                <p className="text-[11px] text-red-400 mt-1 ml-1">Enter a valid phone number</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── STEP 1: Service Selection ─── */}
      {step === 1 && (
        <div className="space-y-5" data-testid="quote-step-services">
          <div className="text-center">
            <p className="text-[10px] sm:text-[11px] font-bold text-[#07c28e] tracking-[0.2em] uppercase mb-1.5">Step 2 of 6</p>
            <h2 className="text-lg sm:text-xl font-bold text-[#191E29]">What type of fulfillment services are you interested in?</h2>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {SERVICES.map((s) => {
              const active = selected.includes(s.key);
              return (
                <button key={s.key} onClick={() => toggleService(s.key)} data-testid={`quote-service-${s.key}`}
                  className={`flex flex-col items-center gap-2 p-4 sm:p-5 rounded-xl border-2 transition-all duration-200 cursor-pointer group ${active ? 'border-[#07c28e] bg-[#f0fdf9]' : 'border-gray-100 hover:border-[#07c28e] hover:bg-[#f0fdf9]'}`}>
                  <div className="relative">
                    <s.Icon size={32} weight="duotone" className={active ? 'text-[#07c28e]' : 'text-gray-400 group-hover:text-[#07c28e]'} />
                    {active && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-[#07c28e] rounded-full flex items-center justify-center">
                        <svg width="8" height="8" viewBox="0 0 10 8" fill="white"><path d="M1 4L3.5 6.5L9 1" strokeWidth="2" stroke="white" fill="none"/></svg>
                      </div>
                    )}
                  </div>
                  <span className={`text-[11px] sm:text-[12px] font-semibold text-center leading-tight ${active ? 'text-[#191E29]' : 'text-gray-500 group-hover:text-[#191E29]'}`}>{s.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── STEP 2: Platform Selection ─── */}
      {step === 2 && (
        <div className="space-y-5" data-testid="quote-step-platforms">
          <div className="text-center">
            <p className="text-[10px] sm:text-[11px] font-bold text-[#07c28e] tracking-[0.2em] uppercase mb-1.5">Step 3 of 6</p>
            <h2 className="text-lg sm:text-xl font-bold text-[#191E29]">Which platforms are you currently using?</h2>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {PLATFORMS.map((p) => {
              const active = platforms.includes(p.key);
              return (
                <button key={p.key} onClick={() => togglePlatform(p.key)} data-testid={`quote-platform-${p.key}`}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer group ${active ? 'border-[#07c28e] bg-[#f0fdf9]' : 'border-gray-100 hover:border-[#07c28e] hover:bg-[#f0fdf9]'}`}>
                  {p.img ? (
                    <img src={p.img} alt={p.label} className="w-8 h-8 object-contain" />
                  ) : (
                    <ShoppingBag size={28} weight="duotone" className={active ? 'text-[#07c28e]' : 'text-gray-400'} />
                  )}
                  <span className={`text-[10px] sm:text-[11px] font-semibold text-center leading-tight ${active ? 'text-[#191E29]' : 'text-gray-500'}`}>{p.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── STEP 3: Monthly Volume ─── */}
      {step === 3 && (
        <div className="space-y-5" data-testid="quote-step-volume">
          <div className="text-center">
            <p className="text-[10px] sm:text-[11px] font-bold text-[#07c28e] tracking-[0.2em] uppercase mb-1.5">Step 4 of 6</p>
            <h2 className="text-lg sm:text-xl font-bold text-[#191E29]">How many orders do you ship per month?</h2>
          </div>
          <div className="space-y-2.5">
            {VOLUMES.map((v) => (
              <button key={v} onClick={() => setVolume(v)} data-testid={`quote-volume-${v}`}
                className={`w-full text-left px-5 py-3.5 rounded-xl border-2 text-sm font-semibold transition-all duration-200 ${volume === v ? 'border-[#07c28e] bg-[#f0fdf9] text-[#191E29]' : 'border-gray-100 text-gray-500 hover:border-[#07c28e] hover:bg-[#f0fdf9]'}`}>
                {v} orders / month
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ─── STEP 4: Challenges (final step → triggers submit) ─── */}
      {step === 4 && (
        <div className="space-y-5" data-testid="quote-step-challenges">
          <div className="text-center">
            <p className="text-[10px] sm:text-[11px] font-bold text-[#07c28e] tracking-[0.2em] uppercase mb-1.5">Almost Done!</p>
            <h2 className="text-lg sm:text-xl font-bold text-[#191E29]">What is the biggest challenge you're trying to solve right now?</h2>
          </div>
          <div className="space-y-2.5">
            {[
              { key: 'shipping_costs', label: 'Improve Shipping Costs' },
              { key: '2day_shipping', label: 'Lock in 2-Day Shipping' },
              { key: 'cx_ltv', label: 'Improve Customer Experience and LTV' },
            ].map((c) => {
              const active = challenges.includes(c.key);
              return (
                <button key={c.key} onClick={() => toggleChallenge(c.key)} data-testid={`quote-challenge-${c.key}`}
                  className={`w-full flex items-center gap-3 px-5 py-4 rounded-xl border-2 text-sm font-semibold transition-all duration-200 text-left ${active ? 'border-[#07c28e] bg-[#f0fdf9] text-[#191E29]' : 'border-gray-100 text-gray-500 hover:border-[#07c28e] hover:bg-[#f0fdf9]'}`}>
                  <div className={`w-5 h-5 rounded flex-shrink-0 border-2 flex items-center justify-center transition-all ${active ? 'bg-[#07c28e] border-[#07c28e]' : 'border-gray-300'}`}>
                    {active && <svg width="12" height="10" viewBox="0 0 12 10" fill="white"><path d="M1 5L4.5 8.5L11 1" strokeWidth="2" stroke="white" fill="none"/></svg>}
                  </div>
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── STEP 5: Confirmation ─── */}
      {step === 5 && !LOW_QUALITY_VOLUMES.has(volume) && (
        <div className="text-center py-6 space-y-4" data-testid="quote-step-done">
          <div className="w-16 h-16 bg-[#f0fdf9] rounded-full flex items-center justify-center mx-auto">
            <CheckCircle size={40} weight="fill" className="text-[#07c28e]" />
          </div>
          <h2 className="text-xl font-bold text-[#191E29]">You're all set!</h2>
          <p className="text-sm text-gray-500 leading-relaxed max-w-[300px] mx-auto">
            We've got your details. Book a time with Matt to get your free fulfillment audit and live quote.
          </p>
          <p className="text-xs text-gray-400">We'll be in touch at <strong className="text-[#191E29]">{form.email}</strong></p>
          <div className="pt-2">
            <p className="text-xs text-gray-400 mb-3">Want to skip the wait?</p>
            <a
              href="https://calendly.com/tanner-sv/30"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="quote-book-call-btn"
              className="inline-flex items-center justify-center gap-2 w-full py-3.5 bg-[#07c28e] text-white font-bold text-base rounded-xl hover:bg-[#06a879] transition-colors duration-300"
            >
              <CalendarBlank size={18} weight="bold" />
              Book a Call Now
            </a>
          </div>
        </div>
      )}

      {/* ─── STEP 5 (LOW-QUALITY VARIANT): Referred to Fulfilment.com partner ─── */}
      {step === 5 && LOW_QUALITY_VOLUMES.has(volume) && (
        <div className="text-center py-6 space-y-4" data-testid="quote-step-done-referred">
          <div className="w-16 h-16 bg-[#f0fdf9] rounded-full flex items-center justify-center mx-auto">
            <CheckCircle size={40} weight="fill" className="text-[#07c28e]" />
          </div>
          <h2 className="text-xl font-bold text-[#191E29]">Thanks, {form.name.split(' ')[0] || 'there'}!</h2>
          <p className="text-sm text-gray-500 leading-relaxed max-w-[340px] mx-auto">
            We don't have capacity to onboard brands at your current scale right now — but we've just introduced you to our friends at <strong className="text-[#191E29]">Fulfilment.com</strong>, who are excellent at matching brands your size with well-priced 3PLs.
          </p>
          <p className="text-xs text-gray-400">Check your inbox at <strong className="text-[#191E29]">{form.email}</strong> for the intro.</p>
        </div>
      )}

      {/* ─── Navigation Buttons ─── */}
      {step < 5 && (
        <div className="flex gap-3 mt-6">
          {step > 0 && (
            <button onClick={() => setStep((s) => s - 1)} data-testid="quote-back-btn"
              className="flex items-center justify-center gap-1 px-5 py-3.5 border-2 border-gray-200 text-gray-500 font-semibold text-sm rounded-xl hover:border-gray-300 transition-colors">
              <ArrowLeft size={14} /> Back
            </button>
          )}
          <button onClick={next} disabled={!canNext() || submitting} data-testid="quote-next-btn"
            className={`flex-1 flex items-center justify-center gap-2 py-3.5 font-bold text-base rounded-xl transition-all duration-300 ${canNext() && !submitting ? 'bg-[#191E29] text-white hover:bg-[#07c28e] cursor-pointer' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>
            {submitting ? 'Submitting...' : step === 4 ? 'Get My Quote' : 'Next'}
            {!submitting && step < 4 && <ArrowRight size={14} weight="bold" />}
          </button>
        </div>
      )}
    </div>
  );
}
